import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { ConnectionType, Network } from '@capacitor/network';
import { TranslateService } from '@ngx-translate/core';

import { withChange } from '@core/utils/signal';

import { LogService } from '../log/log';
import { ToastService } from '../toast/toast';

@Injectable({
  providedIn: 'root'
})
export class NetworkService {
  private readonly toastService = inject(ToastService);
  private readonly logService = inject(LogService);
  private readonly translateService = inject(TranslateService);

  private readonly connected$ = signal<boolean>(true);
  readonly connected = this.connected$.asReadonly();
  private readonly connectionType$ = signal<ConnectionType | null>(null);
  readonly connectionType = this.connectionType$.asReadonly();
  readonly connectedChange = withChange(this.connected);

  constructor() {
    effect(() => {
      const { previous, current } = this.connectedChange();
      untracked(() => {
        const header = this.translateService.instant('Network.Network_Status');
        if (!previous && current) {
          const message = this.translateService.instant('Network.Network_Connected');
          this.toastService.success(header, message);
        } else if (previous && !current) {
          const message = this.translateService.instant('Network.Network_Disconnected');
          this.toastService.error(header, message);
        }
      });
    });

    this.init();
  }

  async init() {
    const status = await Network.getStatus();
    this.connected$.set(status.connected);
    this.connectionType$.set(status.connectionType);

    Network.addListener('networkStatusChange', (status) => {
      this.logService.info('[NetworkService] Network status changed', status);
      this.connected$.set(status.connected);
      this.connectionType$.set(status.connectionType);
    });
  }
}
